import { AUTH_ROUTES } from '@constants/auth-routes';
import { getSafeLoginReturnUrl } from '@helpers/auth-redirect';
import { PRIVATE_TYPE, USER_MENU_ITEMS, type MenuItem } from '@models/menu';
import type { SessionInfoResponse } from '@models/user';

/** Role key used by `USER_MENU_ITEMS` (matches root `main.ts` `role` computed). */
export type UserMenuRoleKey = PRIVATE_TYPE | 'guest';

/** Legacy string keys on `USER_MENU_ITEMS` from root `main.ts`. */
export type LegacyUserMenuRole = 'patient' | 'providers' | 'staff' | 'guest';

const STAFF_GROUP_HANDLES = [
  'providerUsers',
  'medicalSupport',
  'medicalSupportManagers',
  'customerSupport',
  'customerSupportManagers',
] as const;

export function getUserMenuRoleFromSession(
  info?: SessionInfoResponse | null,
): UserMenuRoleKey {
  if (!info || info.isGuest) {
    return 'guest';
  }

  if (info.groups?.includes('patients') || info.isPatient) {
    return PRIVATE_TYPE.PATIENT;
  }

  if (info.groups?.includes('providers') || info.isProvider) {
    return PRIVATE_TYPE.MANAGER;
  }

  if (STAFF_GROUP_HANDLES.some((handle) => info.groups?.includes(handle))) {
    return PRIVATE_TYPE.STAFF;
  }

  if (info.isProviderUser) {
    return PRIVATE_TYPE.STAFF;
  }

  return PRIVATE_TYPE.PATIENT;
}

export function getLegacyUserMenuRoleKey(
  info?: SessionInfoResponse | null,
): LegacyUserMenuRole {
  const role = getUserMenuRoleFromSession(info);
  if (role === 'guest') {
    return 'guest';
  }
  if (role === PRIVATE_TYPE.PATIENT) {
    return 'patient';
  }
  if (role === PRIVATE_TYPE.MANAGER) {
    return 'providers';
  }
  return 'staff';
}

export function getUserMenuItemsForSession(
  info?: SessionInfoResponse | null,
): MenuItem[] {
  const role = getUserMenuRoleFromSession(info);
  if (role === 'guest') {
    return [];
  }

  return USER_MENU_ITEMS[role] ?? USER_MENU_ITEMS[PRIVATE_TYPE.PATIENT];
}

/** Dashboard URL for the logged-in user (patient / manager / staff). */
export function getAccountDashboardUrl(
  info?: SessionInfoResponse | null,
): string {
  const items = getUserMenuItemsForSession(info);
  const dashboard = items.find((item) => item.name === 'My dashboard');
  return dashboard?.path ?? AUTH_ROUTES.LOGIN;
}

/** After login or sign-up: honour `?return=` then role dashboard. */
export function getPostLoginRedirectUrl(
  info?: SessionInfoResponse | null,
): string {
  const returnTo = getSafeLoginReturnUrl();
  if (returnTo) {
    return returnTo;
  }

  return getAccountDashboardUrl(info);
}
